import React, { useState, useEffect } from "react";
import axios from "axios";
import { HeartPulse, AlertTriangle, ShieldCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { ArticleStrip } from "../components/ArticleStrip";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HealthPage() {
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/health-topics`).then((r) => setTopics(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHero topic="physiotherapy athlete recovery" title="Sports Health, Medicine & Wellbeing" subtitle="Educational content on injury prevention, recovery concepts and athlete wellbeing — reviewed for safety, never a substitute for professional care." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="medical-disclaimer">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <p className="text-sm text-red-800"><T>This section is educational only. It does not diagnose injuries, clear athletes to return to play, or prescribe treatment. In an emergency, contact your local emergency services immediately.</T></p>
        </div>
        <AIContent topic="Foundations of sports health — prevention, recovery and athlete wellbeing" context="General education on injury prevention, training load, sleep, nutrition basics and mental wellbeing. No diagnosis, no individualized advice, remind readers to consult qualified professionals." />
        <ArticleStrip tag="Health" title="Health Features" limit={3} />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="health-topics-grid">
          {topics.map((t) => (
            <div key={t.id} data-testid={`health-topic-${t.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500 text-white"><HeartPulse className="h-5 w-5" /></span>
                <div className="flex gap-1.5">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase text-slate-600"><T>{t.category}</T></span>
                  <span className="rounded-full bg-purple-100 px-2.5 py-1 text-[10px] font-bold uppercase text-purple-700">{t.tier}</span>
                </div>
              </div>
              <h3 className="mt-3 text-lg font-bold text-slate-900"><T>{t.title}</T></h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600"><T>{t.overview}</T></p>
              <button data-testid={`health-topic-toggle-${t.id}`} onClick={() => setOpen(open === t.id ? null : t.id)} className="mt-3 text-xs font-bold text-[#0055FF] hover:underline">
                {open === t.id ? <T>Hide details</T> : <T>Signs, prevention & urgent warnings</T>}
              </button>
              {open === t.id && (
                <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-3">
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500"><T>Common signs</T></p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">{t.signs.map((x) => <li key={x}>• <T>{x}</T></li>)}</ul>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500"><T>Prevention</T></p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">{t.prevention.map((x) => <li key={x}>• <T>{x}</T></li>)}</ul>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-red-500"><T>Urgent warning signs</T></p>
                    <ul className="mt-2 space-y-1 text-xs text-red-700">{t.urgent.map((x) => <li key={x}>• <T>{x}</T></li>)}</ul>
                  </div>
                </div>
              )}
              <p className="mt-4 flex items-center gap-1.5 border-t border-slate-100 pt-3 text-[11px] text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5" /> <T>Reviewed content class — educational; consult qualified professionals.</T>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
